import { db } from '@/integrations/idb/db';
import { setUserPermissions } from '@/integrations/idb/models/permissions';
import { addUser, getCurrentUser } from '@/integrations/idb/models/users';
import { Sentry } from '@/integrations/sentry/sentry';
import * as accountMaintenance from '@/shared/account-maintenance';

type Identity = { id: string; tenantId: string };

const isSameIdentity = (a: Identity, b: Identity) =>
  a.id === b.id && a.tenantId === b.tenantId;

export class SessionConflictError extends Error {
  constructor() {
    super('Another identity is already signed in on this browser');
    this.name = 'SessionConflictError';
  }
}

type AuthSession = {
  id: string;
  name: string;
  email: string;
  token: string;
  refreshToken: string;
  roles: string[];
  tenantId: string;
  apiPermissions: string[];
};

export const establishSession = async ({
  id,
  name,
  email,
  token,
  refreshToken,
  roles,
  tenantId,
  apiPermissions,
}: AuthSession) => {
  await db.transaction('rw', db.users, async () => {
    const existing = await getCurrentUser();

    if (existing?.token && !isSameIdentity(existing, { id, tenantId })) {
      throw new SessionConflictError();
    }

    await addUser({ id, name, email, token, refreshToken, roles, tenantId });
  });

  await setUserPermissions(id, apiPermissions);

  await accountMaintenance
    .queryUserDetail(id)
    .then((detail) =>
      db.users.update(id, {
        practitionerTypeCode: detail.practitionerTypeCode ?? '',
        practitionerTypeName: detail.practitionerTypeName ?? '',
        superAdminFlag: detail.superAdminFlag,
      }),
    )
    .catch((error) => console.error(error));

  Sentry.setUser({ username: name, email });
};
